import { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { isAuthConfigured } from '@/auth/config';
import { cn } from '@/lib/utils';

/**
 * Who you are signed in as, and the way out (M0-24).
 *
 * Built on plain state rather than a dropdown library: it is one menu, and the
 * behaviours that matter — Escape closes it, clicking away closes it, the
 * trigger reports its state — are a few lines each and do not justify a
 * dependency the rest of the app would not use.
 */
export function UserMenu() {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const { data: me } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const account = instance.getActiveAccount();
  // Prefer our own record; fall back to the token's account while /me is in
  // flight, so the menu never renders as an anonymous blank.
  const name = me?.name ?? account?.name ?? account?.username ?? t('userMenu.signedInFallback');
  const email = me?.email ?? account?.username ?? '';
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!isAuthConfigured) {
    return null;
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-[var(--color-muted)] focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="user-menu-trigger"
        onClick={() => setOpen((value) => !value)}
      >
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)] text-xs font-medium text-[var(--color-primary-foreground)]"
          aria-hidden="true"
        >
          {initials || '·'}
        </span>
        <span className="hidden max-w-[10rem] truncate sm:inline">{name}</span>
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={t('common.account')}
          className={cn(
            'absolute right-0 z-50 mt-1 w-64 rounded-lg border bg-[var(--color-card)] p-1 shadow-lg',
          )}
        >
          <div className="border-b px-3 py-2">
            <p className="truncate text-sm font-medium">{name}</p>
            {email ? (
              <p className="truncate text-xs text-[var(--color-muted-foreground)]">{email}</p>
            ) : null}
            {me?.role ? (
              <span className="mt-2 inline-block rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-xs">
                {me.role}
              </span>
            ) : null}
          </div>
          {me?.userId ? (
            // The one nav entry point every role can reach their own
            // technician profile from (M1-24) — `/people` is `users.read`-gated
            // and a Technician holds no such permission, so this link, not
            // that list, is how they ever get to `/technicians/:userId`.
            <Link
              to={`/technicians/${me.userId}`}
              role="menuitem"
              className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-[var(--color-muted)]"
              onClick={() => setOpen(false)}
            >
              {t('userMenu.myProfile')}
            </Link>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-[var(--color-muted)]"
            onClick={() => void instance.logoutRedirect()}
          >
            {t('userMenu.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
